import { useTranslation } from "react-i18next";
import { Box, Checkbox, Chip, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { Tooltip } from "@gisce/oficina-virtual-components";
import { DownloadButton } from "../components/DownloadButton";

const compareText = (leftValue, rightValue, desc) => {
  const left = (leftValue || "").toString().toLowerCase();
  const right = (rightValue || "").toString().toLowerCase();

  return desc
    ? right.localeCompare(left)
    : left.localeCompare(right);
};

const compareDate = (leftValue, rightValue, desc) => {
  const left = leftValue ? new Date(leftValue).getTime() : 0;
  const right = rightValue ? new Date(rightValue).getTime() : 0;

  return desc ? right - left : left - right;
};

const getValuesText = (values = []) => values.filter(Boolean).join(', ');

const renderRelatedValues = (
  values = [],
  singularLabel,
  pluralLabel
) => {
  const cleanValues = Array.from(new Set((values || []).filter(Boolean)));

  if (!cleanValues.length) {
    return '';
  }

  const [firstValue, ...restValues] = cleanValues;
  const tooltipTitle = (
    <Stack spacing={0.5}>
      <Typography variant="body2" fontWeight={600}>
        {cleanValues.length === 1 ? singularLabel : pluralLabel}
      </Typography>
      {cleanValues.map((value) => (
        <Typography
          key={value}
          variant="body2"
          sx={{ wordBreak: 'break-word' }}
        >
          {value}
        </Typography>
      ))}
    </Stack>
  );

  return (
    <Tooltip title={tooltipTitle}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          maxWidth: '100%',
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {firstValue}
        </Typography>
        {!!restValues.length && (
          <Chip
            size="small"
            variant="outlined"
            label={`+${restValues.length}`}
            sx={{ height: 22 }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const useDocumentRepositoryTable = ({
  selectedAttachmentIds,
  hasVisibleAttachments,
  isAllVisibleSelected,
  toggleAttachmentSelection,
  toggleAllVisibleSelection,
}) => {
  const { t } = useTranslation();
  const loaded = useSelector((state) => state.documentRepository.loaded);

  const columns = [
    {
      title: (
        <Checkbox
          size="small"
          disabled={!hasVisibleAttachments}
          checked={isAllVisibleSelected}
          indeterminate={selectedAttachmentIds.length > 0 && !isAllVisibleSelected}
          onChange={toggleAllVisibleSelection}
        />
      ),
      key: "selection",
      computeValue: (attachment) => (
        <Checkbox
          size="small"
          checked={selectedAttachmentIds.includes(attachment.rowId)}
          onChange={() => toggleAttachmentSelection(attachment.rowId)}
        />
      )
    },
    {
      title: t('common:text.document_repository_attachment_name'),
      key: "name",
      computeValue: (attachment) => attachment.name,
      sorter: (a, b, column, desc) => compareText(a.name, b.name, desc),
    },
    {
      title: t('common:text.document_repository_attachment_contract'),
      key: "contract_name",
      computeValue: (attachment) => renderRelatedValues(
        attachment.contract_names,
        t('common:text.document_repository_contract_single'),
        t('common:text.document_repository_contract_multiple')
      ),
      sorter: (a, b, column, desc) => compareText(
        getValuesText(a.contract_names),
        getValuesText(b.contract_names),
        desc
      ),
    },
    {
      title: t('common:text.document_repository_attachment_associated_object'),
      key: "associated_object",
      computeValue: (attachment) => renderRelatedValues(
        attachment.associated_objects,
        t('common:text.document_repository_related_object_single'),
        t('common:text.document_repository_related_object_multiple')
      ),
      sorter: (a, b, column, desc) => compareText(
        getValuesText(a.associated_objects),
        getValuesText(b.associated_objects),
        desc
      ),
    },
    {
      title: t('common:text.document_repository_attachment_date'),
      key: "created_date",
      computeValue: (attachment) => attachment.created_date,
      sorter: (a, b, column, desc) => compareDate(
        a.created_date_sort,
        b.created_date_sort,
        desc
      ),
    }, {
      title: t('common:text.document_repository_attachment_actions'),
      key: "actions",
      computeValue: (attachment) => <>
        <DownloadButton 
          attachment={attachment} 
        />
      </>
    }
  ].filter(Boolean);

  return {
    columns, loaded
  }
}

export default useDocumentRepositoryTable;
